import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { Activity, Headphones, HeadphoneOff, Maximize2, MessageSquare, Mic, MicOff, Monitor, MonitorOff, MoreVertical, PhoneOff, Users, Video, VideoOff, Volume2, VolumeX, X } from 'lucide-react'
import type { Track } from 'livekit-client'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { isUnread } from '@/lib/guildUi'
import { desktop } from '@/lib/desktop'
import { useVoiceStore } from '@/store/voice'
import { guildApi } from '@/lib/guildApi'
import { describeError, publicUser } from '@/lib/auth'
import { P, channelPermissions, has, roleColor } from '@/lib/permissions'
import { memberName, plural } from '@/lib/guildUi'
import type { AvatarDecoration, Member, VoiceState } from '@/types/api'
import { cx } from '@/lib/cx'
import { fullscreenTileVideo } from '@/lib/fullscreen'
import { TileFullscreenButton } from './TileFullscreenButton'
import { canShareScreen } from '@/lib/screenShare'
import { streamLabel } from '@/lib/streamView'
import { memberAvatarUrl } from '@/lib/avatar'
import { userContextHandler } from '@/store/contextMenu'
import { ScreenShareModal } from './ScreenShareModal'
import { StreamStats } from './StreamStats'
import { CameraFlip } from './CameraFlip'
import { EffectMark } from './EffectMark'
import { StreamStage } from './StreamStage'
import { LiveBadge, StreamPlaceholder } from './StreamPreview'
import { StreamViewers, type Viewer } from './StreamViewers'
import { TrackVideo } from './TrackVideo'
import { TilePip } from './TilePip'
import { Avatar } from '../Avatar'
import chatStyles from '../ChatView.module.css'
import styles from './VoiceRoom.module.css'
import { MobileBack } from '@/components/MobileBack'
import { StyledName } from '../StyledName'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/**
 * Голосовой канал: участники плитками, трансляции отдельными плитками, кнопки подключения,
 * микрофона, звука, демонстрации. Открытая трансляция разворачивается на всё место (StreamStage).
 */
export function VoiceRoom({ guildId, channelId }: { guildId: string; channelId: string }) {
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channel = useGuildStore((s) => s.channels[guildId]?.[channelId])
  const members = useGuildStore((s) => s.members[guildId])
  const voiceStates = useGuildStore((s) => s.voiceStates[guildId])
  const showMembers = useAppStore((s) => s.showMembers)
  const toggleMembers = useAppStore((s) => s.toggleMembers)
  const voiceChatOpen = useAppStore((s) => s.voiceChatChannelId === channelId && !s.searchQuery)
  const setVoiceChatChannelId = useAppStore((s) => s.setVoiceChatChannelId)
  const readState = useSocialStore((s) => s.readStates[channelId])
  const connection = useVoiceStore((s) => s.connection)
  const afk = useVoiceStore(s => s.afk)
  const selfMute = useVoiceStore((s) => s.selfMute)
  const selfDeaf = useVoiceStore((s) => s.selfDeaf)
  const sharing = useVoiceStore((s) => s.sharing)
  const camera = useVoiceStore((s) => s.camera)
  const speaking = useVoiceStore((s) => s.speaking)
  const screens = useVoiceStore((s) => s.screens)
  const watching = useVoiceStore((s) => s.watching)
  const live = useVoiceStore((s) => s.live)
  const focus = useVoiceStore((s) => s.focus)
  const streamAudio = useVoiceStore((s) => s.streamAudio)
  const strip = useVoiceStore((s) => s.strip)
  const cleanAudio = useVoiceStore((s) => s.cleanAudio)
  const ownAudioRestricted = useVoiceStore((s) => s.ownAudioRestricted)
  const streamVolumes = useVoiceStore((s) => s.prefs.streamVolumes)
  const sharePrefs = useVoiceStore((s) => s.prefs.screenShare)
  const [shareModal, setShareModal] = useState(false)
  const cameras = useVoiceStore((s) => s.cameras)
  const error = useVoiceStore((s) => s.error)
  const micError = useVoiceStore((s) => s.micError)
  const micHint = useVoiceStore((s) => s.micHint)
  const hintKind = useVoiceStore((s) => s.hintKind)
  const { join, leave, toggleMute, toggleDeaf, toggleCamera, startScreen, stopScreen, retryMic, watchStream, stopWatching, setFocus, toggleStreamMute } = useVoiceStore()
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [menu, setMenu] = useState<string | null>(null)
  const [stats, setStats] = useState<string | null>(null)

  // меню действий на плитке закрывается нажатием мимо (в том числе правой кнопкой) и по Esc;
  // нажатие на саму «⋮» не считается «мимо» — её щелчок и так переключает меню
  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      const at = e.target as Element | null
      if (at?.closest(`[data-tile-menu="${menu}"]`)) return
      setMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const participants = useMemo(() => Object.values(voiceStates ?? {}).filter((v) => v.channel_id === channelId), [voiceStates, channelId])
  const self: Member | undefined = me ? (members?.[me.id] ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }) : undefined
  const perms = guild && self ? channelPermissions(guild, self, channel) : 0n
  const canConnect = has(perms, P.CONNECT)
  // право есть, а захвата в браузере нет — кнопку не показываем, а не показываем ошибку по тапу
  const canStream = has(perms, P.STREAM) && canShareScreen
  const canSpeak = has(perms, P.SPEAK)
  const canMute = has(perms, P.MUTE_MEMBERS)
  const canDeafen = has(perms, P.DEAFEN_MEMBERS)
  const canMove = has(perms, P.MOVE_MEMBERS)
  const connected = connection?.channelId === channelId
  const limit = channel?.user_limit ?? 0
  const full = limit > 0 && participants.length >= limit && !canMove && !connected

  /*
   * Кто показывает экран. Пока мы в комнате — по публикациям LiveKit: он узнаёт первым и не
   * покажет трансляцию, которая умерла вместе с приложением показывающего. Снаружи комнаты
   * LiveKit не видно — там по состоянию голоса с сервера.
   */
  const streamers = participants.filter((vs) => (connected ? vs.user_id in live : vs.self_stream))
  const focused = focus && streamers.some((vs) => vs.user_id === focus) ? focus : null

  const memberOf = (vs: VoiceState) => members?.[vs.user_id] ?? vs.member
  const nameOf = (vs: VoiceState) => {
    const m = memberOf(vs)
    return m ? memberName(m) : vs.user_id
  }
  // зритель трансляции — участник этой же комнаты
  const viewerOf = (id: string): Viewer => {
    const vs = participants.find((v) => v.user_id === id)
    const m = vs ? memberOf(vs) : members?.[id]
    return { name: vs ? nameOf(vs) : m ? memberName(m) : 'Участник', avatar: memberAvatarUrl(guildId, m), decoration: m?.user.avatar_decoration_data, color: colorOf(id) }
  }

  const connect = async () => {
    setBusy(true)
    setActionError(null)
    try {
      await join(guildId, channelId)
    } catch {
      /* ошибка уже в сторе */
    } finally {
      setBusy(false)
    }
  }
  // смотреть трансляцию можно только из комнаты: снаружи сначала подключаемся, как в Discord
  const watch = async (userId: string) => {
    if (!connected) {
      await connect()
      const s = useVoiceStore.getState()
      if (s.connection?.channelId !== channelId || !(userId in s.live)) return
    }
    watchStream(userId)
  }
  const moderate = async (vs: VoiceState, patch: { mute?: boolean; deaf?: boolean; channel_id?: null }) => {
    setMenu(null)
    try {
      await guildApi.updateMember(guildId, vs.user_id, patch)
    } catch (err) {
      setActionError(describeError(err).message)
    }
  }

  if (!guild || !channel) return <main className={chatStyles.chat} />
  const name = channel.name ?? ''
  const count = participants.length
  const watchBlocked = connected ? undefined : !canConnect ? 'Нет права подключаться к этому каналу' : full ? 'Канал заполнен' : undefined
  // в канале никого, и нас в нём нет — вместо пустой сцены заставка с кнопкой, как у Discord после выхода
  const idle = !connected && count === 0
  const joinBlocked = !canConnect ? 'У вас нет права подключаться к этому каналу' : full ? 'Канал заполнен' : null
  const joinLabel = busy ? 'Подключаем…' : connection ? 'Перейти в этот канал' : 'Подключиться к каналу'

  const controlButtons = (
    <>
      <button type="button" className={cx(styles.control, (afk || selfMute) && styles.controlOff)} disabled={afk} data-tooltip={afk ? 'Микрофон выключен в AFK' : selfMute ? 'Включить микрофон' : 'Выключить микрофон'} aria-label={afk ? 'Микрофон выключен в AFK' : selfMute ? 'Включить микрофон' : 'Выключить микрофон'} aria-pressed={afk || selfMute} onClick={() => void toggleMute()}>
        {afk || selfMute ? <MicOff size={22} /> : <Mic size={22} />}
      </button>
      <button type="button" className={cx(styles.control, (afk || selfDeaf) && styles.controlOff)} disabled={afk} data-tooltip={afk ? 'Звук выключен в AFK' : selfDeaf ? 'Включить звук' : 'Выключить звук'} aria-label={afk ? 'Звук выключен в AFK' : selfDeaf ? 'Включить звук' : 'Выключить звук'} aria-pressed={afk || selfDeaf} onClick={() => void toggleDeaf()}>
        {afk || selfDeaf ? <HeadphoneOff size={22} /> : <Headphones size={22} />}
      </button>
      {canSpeak ? (
        <button type="button" className={cx(styles.control, camera && styles.controlOn)} data-tooltip={camera ? 'Выключить камеру' : 'Включить камеру'} aria-label={camera ? 'Выключить камеру' : 'Включить камеру'} aria-pressed={camera} disabled={afk} onClick={() => void toggleCamera()}>
          {camera ? <VideoOff size={22} /> : <Video size={22} />}
        </button>
      ) : null}
      <CameraFlip className={styles.control} size={22} />
      {canStream && !afk ? (
        <button type="button" className={cx(styles.control, sharing && styles.controlOn)} data-tooltip={sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'} aria-label={sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'} aria-pressed={sharing} onClick={() => { if (sharing) void stopScreen(); else setShareModal(true) }}>
          {sharing ? <MonitorOff size={22} /> : <Monitor size={22} />}
        </button>
      ) : null}
      <button type="button" className={cx(styles.control, styles.controlDanger)} data-tooltip="Отключиться" aria-label="Отключиться" onClick={() => void leave()}>
        <PhoneOff size={22} />
      </button>
    </>
  )

  // на заставке ошибка — под кнопкой, которую только что нажали, а не полосой внизу
  const problem = error || micError || actionError ? (
    <p className={styles.error} role="alert">
      {actionError ?? error ?? micError}
      {micHint ? <span className={styles.errorHint}>{micHint}</span> : null}
      {micError ? <button type="button" className={styles.retry} onClick={() => void retryMic()}>Попробовать снова</button> : null}
      {micHint && desktop ? <button type="button" className={styles.retry} onClick={() => desktop?.openPrivacySettings(hintKind === 'camera' ? 'camera' : 'microphone')}>Настройки доступа</button> : null}
    </p>
  ) : null

  const focusedState = focused ? participants.find((vs) => vs.user_id === focused) : undefined
  const stage = focused && focusedState ? (
    <StreamStage
      userId={focused}
      name={nameOf(focusedState)}
      avatar={memberAvatarUrl(guildId, memberOf(focusedState))}
      decoration={memberOf(focusedState)?.user.avatar_decoration_data}
      viewers={<StreamViewers streamerId={focused} who={viewerOf} />}
      color={colorOf(focused)}
      channelName={name}
      title={live[focused] ?? ''}
      own={focused === me?.id}
      track={screens[focused]}
      audio={Boolean(streamAudio[focused])}
      onContextMenu={userContextHandler(focused, { user: memberOf(focusedState)?.user, guildId })}
      strip={participants.map((vs) => {
        const other = vs.user_id !== focused && streamers.includes(vs)
        const cam = cameras[vs.user_id]
        const label = nameOf(vs)
        return (
          <div key={vs.user_id} className={styles.stripItem}>
            {/* чужая трансляция в полоске — кнопкой: переключиться на неё одним нажатием */}
            {other ? (
              <button type="button" className={cx(styles.stripTile, styles.stripStream)} data-tooltip={`Смотреть: ${streamLabel(label)}`} aria-label={`Смотреть: ${streamLabel(label)}`} onClick={() => void watch(vs.user_id)}>
                <Avatar initial={label.slice(0, 1).toUpperCase()} color={colorOf(vs.user_id)} size={28} src={memberAvatarUrl(guildId, memberOf(vs))} decoration={memberOf(vs)?.user.avatar_decoration_data} />
                <LiveBadge className={styles.stripLive} />
              </button>
            ) : null}
            <div className={cx(styles.stripTile, speaking[vs.user_id] && styles.stripSpeaking)} data-user-id={vs.user_id} data-tooltip={label} onContextMenu={userContextHandler(vs.user_id, { user: memberOf(vs)?.user, guildId })}>
              {cam ? <TrackVideo track={cam} className={styles.stripCamera} /> : <Avatar initial={label.slice(0, 1).toUpperCase()} color={colorOf(vs.user_id)} size={32} src={memberAvatarUrl(guildId, memberOf(vs))} decoration={memberOf(vs)?.user.avatar_decoration_data} />}
              <span className={styles.stripName}>
                {label}
                {vs.afk || vs.mute || vs.self_mute ? <MicOff size={12} aria-label="Микрофон выключен" /> : null}
              </span>
            </div>
          </div>
        )
      })}
      controls={<div className={styles.stageControls}>{controlButtons}</div>}
      onCollapse={() => setFocus(null)}
      onClose={() => (focused === me?.id ? setFocus(null) : stopWatching(focused))}
    />
  ) : null

  return (
    <main className={cx(chatStyles.chat, idle && styles.idleSurface)} aria-label={`Голосовой канал ${name}`}>
      {idle ? <span className={styles.idleGlow} aria-hidden="true" /> : null}
      <header className={cx(chatStyles.header, idle && styles.idleHeader)}>
        <MobileBack />
        <Volume2 size={24} className={chatStyles.hash} />
        <h1 className={cx(chatStyles.title, chatStyles.titleText)}>{name}</h1>
        <span className={styles.counter}>{count}{limit ? ` / ${limit}` : ''} {plural(count, 'участник', 'участника', 'участников')}</span>
        <div className={chatStyles.tools}>
          <button type="button" id={`voice-chat-button-${channelId}`} className={cx(chatStyles.iconButton, styles.chatButton, voiceChatOpen && styles.toolActive)} data-tooltip={voiceChatOpen ? 'Закрыть чат' : 'Открыть чат'} aria-label={voiceChatOpen ? 'Закрыть чат канала' : 'Открыть чат канала'} aria-expanded={voiceChatOpen} aria-controls={voiceChatOpen ? `voice-chat-${channelId}` : undefined} onClick={() => {
            useAppStore.setState({ searchQuery: null, threadsPanel: null })
            setVoiceChatChannelId(voiceChatOpen ? null : channelId)
          }}>
            <MessageSquare size={22} />
            {isUnread(channel, readState) ? <span className={styles.chatUnread} aria-label="Есть непрочитанные сообщения" /> : null}
          </button>
          <button type="button" className={cx(chatStyles.iconButton, showMembers && !voiceChatOpen && styles.toolActive)} data-tooltip="Список участников" aria-label="Список участников" aria-pressed={showMembers && !voiceChatOpen} onClick={() => {
            if (voiceChatOpen) useAppStore.setState({ voiceChatChannelId: null, showMembers: true })
            else toggleMembers()
          }}>
            <Users size={22} />
          </button>
        </div>
      </header>

      {stage ?? (idle ? (
        <div className={styles.idle}>
          <div className={styles.idleBody}>
            <h2 className={styles.idleTitle}>{name}</h2>
            <p className={styles.idleText}>Сейчас в канале никого нет</p>
            {joinBlocked ? <p className={styles.idleNote}>{joinBlocked}</p> : null}
            <button type="button" className={styles.join} disabled={joinBlocked !== null || busy} onClick={() => void connect()}>{joinLabel}</button>
            {problem}
          </div>
        </div>
      ) : (
        <div className={cx(styles.stage, 'scroller')}>
          {count === 0 ? null : (
            <div className={styles.grid}>
              {/* трансляции — отдельными плитками впереди: плитка самого человека остаётся на месте */}
              {streamers.map((vs) => {
                const isMe = vs.user_id === me?.id
                // своя трансляция уже здесь, чужая — только если её открыли
                const track = isMe || watching[vs.user_id] ? screens[vs.user_id] : undefined
                return (
                  <StreamTile
                    key={`stream:${vs.user_id}`}
                    userId={vs.user_id}
                    name={nameOf(vs)}
                    avatar={memberAvatarUrl(guildId, memberOf(vs))}
                    decoration={memberOf(vs)?.user.avatar_decoration_data}
                    viewers={<StreamViewers streamerId={vs.user_id} who={viewerOf} />}
                    title={live[vs.user_id] ?? ''}
                    own={isMe}
                    track={track}
                    audio={Boolean(streamAudio[vs.user_id])}
                    muted={(streamVolumes[vs.user_id] ?? 1) === 0}
                    stats={stats === vs.user_id}
                    blocked={watchBlocked}
                    busy={busy}
                    onWatch={() => void watch(vs.user_id)}
                    onFocus={() => watchStream(vs.user_id)}
                    onStop={() => stopWatching(vs.user_id)}
                    onStats={() => setStats(stats === vs.user_id ? null : vs.user_id)}
                    onMute={() => toggleStreamMute(vs.user_id)}
                    onContextMenu={userContextHandler(vs.user_id, { user: memberOf(vs)?.user, guildId })}
                  />
                )
              })}
              {participants.map((vs) => {
                const m = memberOf(vs)
                const label = nameOf(vs)
                const cam = cameras[vs.user_id]
                const isMe = vs.user_id === me?.id
                // бот без камеры занимает низкую плитку: показывать у него нечего
                const isBot = (m?.user.bot ?? false) && !cam
                const muted = vs.afk || vs.mute || vs.self_mute
                const deaf = vs.afk || vs.deaf || vs.self_deaf
                // себе — только снять серверный мут или звук, если их поставил другой модератор
                const selfFix = isMe && ((vs.mute && canMute) || (vs.deaf && canDeafen))
                const canModerate = selfFix || (!isMe && (canMute || canDeafen || canMove))
                return (
                  <div key={vs.user_id} className={cx(styles.tile, speaking[vs.user_id] && styles.tileSpeaking, isBot && styles.tileBot)} data-user-id={vs.user_id} onDoubleClick={cam ? fullscreenTileVideo : undefined} onContextMenu={userContextHandler(vs.user_id, { user: m?.user, guildId })}>
                    {cam ? <TrackVideo track={cam} className={styles.camera} /> : (
                      <Avatar initial={label.slice(0, 1).toUpperCase()} color={colorOf(vs.user_id)} size={isBot ? 40 : 72} src={memberAvatarUrl(guildId, m)} decoration={m?.user.avatar_decoration_data} />
                    )}
                    <EffectMark userId={vs.user_id} size={30} className={styles.tileEffect} />
                    <div className={styles.tileFooter}>
                      <span className={styles.tileName} style={m ? { color: roleColor(guild.roles, m.roles) } : undefined}><StyledName name={label} style={m?.user.display_name_styles} />{isMe ? ' (вы)' : ''}</span>
                      <span className={styles.tileIcons}>
                        {streamers.includes(vs) ? <LiveBadge /> : null}
                        {muted ? <MicOff size={14} className={vs.mute ? styles.serverMuted : undefined} aria-label={vs.mute ? 'Заглушен на сервере' : 'Микрофон выключен'} /> : null}
                        {deaf ? <HeadphoneOff size={14} className={vs.deaf ? styles.serverMuted : undefined} aria-label={vs.deaf ? 'Звук выключен сервером' : 'Звук выключен'} /> : null}
                      </span>
                    </div>
                    {cam ? (
                      <TileFullscreenButton userId={vs.user_id} className={styles.tileFullscreen} />
                    ) : null}
                    {cam && !isMe ? <TilePip kind="camera" userId={vs.user_id} track={cam} name={label} /> : null}
                    {canModerate ? (
                      <button type="button" className={styles.tileMenu} data-tile-menu={vs.user_id} aria-label={`Действия с ${label}`} aria-haspopup="menu" aria-expanded={menu === vs.user_id} onClick={() => setMenu(menu === vs.user_id ? null : vs.user_id)}>
                        <MoreVertical size={16} />
                      </button>
                    ) : null}
                    {menu === vs.user_id ? (
                      <div className={styles.menu} role="menu" data-tile-menu={vs.user_id}>
                        {canMute && (!isMe || vs.mute) ? <button type="button" role="menuitem" onClick={() => void moderate(vs, { mute: !vs.mute })}>{vs.mute ? 'Снять серверный мут' : 'Заглушить на сервере'}</button> : null}
                        {canDeafen && (!isMe || vs.deaf) ? <button type="button" role="menuitem" onClick={() => void moderate(vs, { deaf: !vs.deaf })}>{vs.deaf ? 'Включить звук' : 'Выключить звук'}</button> : null}
                        {canMove && !isMe ? <button type="button" role="menuitem" className={styles.menuDanger} onClick={() => void moderate(vs, { channel_id: null })}>Отключить от канала</button> : null}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ))}

      {idle ? null : problem}
      {/* в развёрнутой трансляции кнопки голоса живут внутри неё, на заставке — кнопка по центру */}
      {stage || idle ? null : (
        <div className={styles.controls}>
          {connected ? (
            <>
              {/* состояние голоса показано слева в панели пользователя — здесь только то, чего там нет:
                  показывающему важно видеть, что голоса из его трансляции действительно вычитаются */}
              <span className={styles.status}>
                {sharing && (cleanAudio || ownAudioRestricted) ? `Звук компьютера идёт без Кличата${cleanAudio?.playedMs === 0 ? ' (пока тишина)' : ''}` : sharing && strip?.active ? `Голоса из трансляции убраны (−${Math.round(strip.erle)} дБ)` : ''}
              </span>
              {controlButtons}
            </>
          ) : (
            <>
              {joinBlocked ? <span className={styles.status}>{joinBlocked}</span> : null}
              <button type="button" className={styles.connect} disabled={joinBlocked !== null || busy} onClick={() => void connect()}>
                {joinLabel}
              </button>
            </>
          )}
        </div>
      )}
      {shareModal ? <ScreenShareModal initial={sharePrefs} guildId={guildId} onStart={startScreen} onClose={() => setShareModal(false)} /> : null}
    </main>
  )
}

interface StreamTileProps {
  userId: string
  name: string
  avatar: string | undefined
  decoration: AvatarDecoration | null | undefined
  /** Кто смотрит (StreamViewers): глаз с числом рядом с «В ЭФИРЕ». */
  viewers: ReactNode
  title: string
  own: boolean
  /** Дорожка, если её видно: своя всегда, чужая — пока смотрим. */
  track: Track | undefined
  audio: boolean
  muted: boolean
  stats: boolean
  /** Почему смотреть нельзя (нет права, канал заполнен); undefined — можно. */
  blocked: string | undefined
  busy: boolean
  onWatch: () => void
  onFocus: () => void
  onStop: () => void
  onStats: () => void
  onMute: () => void
  /** Правый клик — меню человека с ползунком «Звук трансляции», а не меню видео браузера. */
  onContextMenu: (e: ReactMouseEvent) => void
}

/**
 * Плитка трансляции — отдельно от плитки человека. Пока трансляцию не открыли, на ней заглушка
 * и «Смотреть стрим»: подписки на экран нет, поток не идёт. Открытая — картинка на всю ширину.
 * Рамки «говорит» здесь нет: говорит человек, а не его экран.
 */
function StreamTile({ userId, name, avatar, decoration, viewers, title, own, track, audio, muted, stats, blocked, busy, onWatch, onFocus, onStop, onStats, onMute, onContextMenu }: StreamTileProps) {
  const color = colorOf(userId)
  if (!track) {
    return (
      <div className={cx(styles.tile, styles.streamTile)} data-stream-id={userId} onContextMenu={onContextMenu}>
        <StreamPlaceholder name={name} title={title} avatar={avatar} decoration={decoration} color={color} size={48} className={styles.streamFill}>
          <button type="button" className={styles.watch} disabled={blocked !== undefined || busy} data-tooltip={blocked} onClick={onWatch}>
            {busy ? 'Подключаем…' : 'Смотреть стрим'}
          </button>
        </StreamPlaceholder>
        <span className={styles.tileLive}>
          <LiveBadge />
          {viewers}
        </span>
      </div>
    )
  }
  return (
    <div className={cx(styles.tile, styles.streamTile, !own && styles.tileScreen)} data-stream-id={userId} onDoubleClick={onFocus} onContextMenu={onContextMenu}>
      <TrackVideo track={track} className={styles.video} />
      <div className={styles.tileFooter}>
        <span className={styles.tileName}>{streamLabel(name)}</span>
        <span className={styles.tileIcons}>
          <LiveBadge />
          {viewers}
          {/* тишина в трансляции бывает по двум причинам, и обе надо назвать вслух */}
          {!audio ? (
            <span
              className={styles.streamMuted}
              data-tooltip={own
                ? 'Вы показываете без звука: либо снята галочка «Транслировать звук компьютера», либо система его не отдала'
                : 'Показывают без звука: у них снята галочка «Транслировать звук компьютера» или система его не отдала'}
            >
              <VolumeX size={14} aria-label={own ? 'Вы показываете без звука' : 'Показывают без звука'} />
            </span>
          ) : null}
        </span>
      </div>
      {stats ? <StreamStats track={track} own={own} /> : null}
      <div className={styles.tileTools}>
        {!own && audio ? (
          <button
            type="button"
            className={cx(styles.tileTool, muted && styles.tileToolOff)}
            data-tooltip={muted ? 'Включить звук трансляции' : 'Выключить звук трансляции'}
            aria-label={muted ? 'Включить звук трансляции' : 'Выключить звук трансляции'}
            aria-pressed={muted}
            onClick={onMute}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        ) : null}
        <button type="button" className={styles.tileTool} data-tooltip={stats ? 'Скрыть цифры трансляции' : 'Разрешение, кадры и битрейт'} aria-label="Цифры трансляции" aria-pressed={stats} onClick={onStats}>
          <Activity size={16} />
        </button>
        <button type="button" className={styles.tileTool} data-tooltip="Развернуть" aria-label="Развернуть" onClick={onFocus}>
          <Maximize2 size={16} />
        </button>
      </div>
      {!own ? (
        <button type="button" className={styles.tileClose} data-tooltip="Закрыть просмотр" aria-label="Закрыть просмотр" onClick={onStop}>
          <X size={16} />
        </button>
      ) : null}
    </div>
  )
}
